import * as React from 'react';
import { canUseDOM } from '@shopee/can-use-dom';

interface FocusTrapQueueItem {
  eventListener: (e: Event) => void;
  root: HTMLElement | null;
}

const focusTrapQueue: Array<FocusTrapQueueItem> = [];

export type Props = {
  /**
   * Content inside FocusTrap. Browser focus will be kept inside this element
   */
  children: React.ReactNode;

  /**
   * Element to be rendered as container of `children`.
   *
   * This could be HTML tagName, like 'div', or a React component if tabIndex and ref are forwarded through the component
   */
  element: string | React.ComponentType<any>;

  /**
   * Custom classname to be passed to `element`
   */
  className?: string;

  /**
   * If `autoFocus` is true, will auto focus on this element when mounted
   */
  autoFocus?: boolean;

  nodeRef?: React.MutableRefObject<HTMLElement | null>;

  [otherProp: string]: unknown;
};
/**
 * ## Basic Information

| Key              | Value                                            |
| :--------------- | :----------------------------------------------- |
| Package name     | @stardust/focus-trap                             |
| Package location | packages/stardust/packages/components/focus_trap |

 * FocusTrap is meant to improve accessibility when user presses `Tab` key to focus through elements in a page.
By default, pressing `Tab` key will focus through all inputs & buttons in the page, which is normally okay.
But when there is an active popup on the page, it's better to keep the focus inside the popup, as the content behind the popup are supposed to be inactive.
FocusTrap helps wrap a component to keep the focus inside the component as long as it is mounted.
 */
class FocusTrap extends React.Component<Props> {
  /**
   * root is dependent on what the created element is,
   * and so createRef() will also be dependent on whatever that element is
   */
  root: React.MutableRefObject<HTMLElement | null> =
    React.createRef<HTMLElement>();
  previousFocus: HTMLElement | null = null;
  timer: ReturnType<typeof setTimeout> | null = null;
  lastFocusTrap: FocusTrapQueueItem | null;

  constructor(props: Props) {
    super(props);
    // save current focus for future.
    // will recover focus when unmount.
    if (canUseDOM) {
      this.previousFocus = document.activeElement as HTMLElement;
    }
    this.lastFocusTrap = focusTrapQueue[focusTrapQueue.length - 1];
  }

  componentDidMount(): void {
    if (this.lastFocusTrap) {
      document.removeEventListener('focus', this.lastFocusTrap.eventListener);
    }
    // register focus listener to re-focus when the focus is out of scope
    document.addEventListener('focus', this.onFocus, true);
    // auto focus on mount
    const currentAnchor = this.root.current;
    this.props.autoFocus && currentAnchor && currentAnchor.focus();
    focusTrapQueue.push({
      eventListener: this.onFocus,
      root: currentAnchor,
    });
  }

  componentWillUnmount(): void {
    const thisFocusTrapIndex = focusTrapQueue.findIndex(
      focusTrap => focusTrap.root === this.root.current
    );
    focusTrapQueue.splice(thisFocusTrapIndex, 1);
    document.removeEventListener('focus', this.onFocus, true);
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.recoverPreviousFocus();
    this.previousFocus = null;
  }

  recoverPreviousFocus(): void {
    if (this.previousFocus && this.previousFocus.focus) {
      this.previousFocus.focus();
      if (this.lastFocusTrap) {
        document.addEventListener('focus', this.lastFocusTrap?.eventListener);
      }
    }
  }

  onFocus = (event: Event): void => {
    const currentFocusTarget = event.target;
    const currentAnchor = this.root.current;
    if (
      currentAnchor &&
      currentAnchor.contains &&
      !currentAnchor.contains(currentFocusTarget as Node)
    ) {
      event.preventDefault();
      this.trapFocus();
    }
  };

  trapFocus = (): void => {
    // focus back to root
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      const currentAnchor = this.root.current;
      const currentFocus = document.activeElement;
      if (
        currentAnchor &&
        currentAnchor.contains &&
        !currentAnchor.contains(currentFocus)
      ) {
        currentAnchor.focus();
      }
    }, 10);
  };

  render(): React.ReactElement {
    const { children, element, nodeRef, ...props } = this.props;
    return React.createElement(
      element,
      {
        tabIndex: 0,
        ref: (ref: HTMLElement) => {
          this.root.current = ref;
          if (nodeRef) nodeRef.current = ref;
        },
        ...props,
      },
      children
    );
  }
}

export default FocusTrap;
